import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Popover } from 'antd';
import { EllipsisOutlined, TagOutlined } from '@ant-design/icons';
import { Close } from '@mui/icons-material';

const SelectionActionBar = ({
  selectedRowsCount,
  totalCount,
  isSelectAllActive,
  onBulkTagClick,
  onBulkRemoveTagClick,
  bulkTagPopoverOpen,
  setBulkTagPopoverOpen,
  bulkRemoveTagsOpen,
  setBulkRemoveTagsOpen,
  TagAddContent,
  TagRemoveContent,
  onBulkTagAdd,
  onBulkTagRemove,
  availableTags,
  loadingTags,
  tagsFetchError
}) => {
  const { t } = useTranslation();
  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState(null);
  const [currentAction, setCurrentAction] = React.useState(null); // 'add' or 'remove'
  
  // Determine which count to display - use actual selection count when not select-all active
  const displayCount = isSelectAllActive ? totalCount : selectedRowsCount;
  const displayText = React.useMemo(() => {
    if (displayCount === 1) {
      return t('rowSelected', { count: displayCount });
    }
    return t('rowsSelected', { count: displayCount });
  }, [displayCount, t]);
  
  // Reset action state when popover closes
  React.useEffect(() => {
    if (!popoverAnchorEl) {
      setCurrentAction(null);
    }
  }, [popoverAnchorEl]);
  
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      gap: 2,
      animation: 'slideIn 0.2s ease-out',
      '@keyframes slideIn': {
        from: { opacity: 0, transform: 'translateY(-10px) scale(0.95)' },
        to: { opacity: 1, transform: 'translateY(0) scale(1)' }
      }
    }}>
      {/* Selected count - simple text with circle */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        marginRight: 2
      }}>
        <Box sx={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: '#000000',
          flexShrink: 0
        }} />
        <Typography sx={{
          fontSize: '13px',
          fontWeight: 400,
          color: '#000000',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          letterSpacing: '0.02em'
        }}>
          {displayText}
        </Typography>
      </Box>
      
      {/* Action buttons container */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flex: 1
      }}>
        {/* Add Tags Button */}
        <Popover
          content={
            bulkTagPopoverOpen && TagAddContent ? (
              <TagAddContent
                onConfirm={onBulkTagAdd}
                onCancel={() => {
                  setBulkTagPopoverOpen(false);
                  setCurrentAction(null);
                }}
                availableTags={availableTags}
                existingTags={[]}
                loadingTags={loadingTags}
                tagsFetchError={tagsFetchError}
              />
            ) : null
          }
          trigger="click"
          placement="bottomLeft"
          open={bulkTagPopoverOpen && currentAction === 'add'}
          onOpenChange={(visible) => {
            if (!visible) {
              setBulkTagPopoverOpen(false);
              setCurrentAction(null);
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              padding: '4px 10px',
              borderRadius: '4px',
              backgroundColor: 'rgba(55, 53, 47, 0.03)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              color: '#000000',
              height: '28px',
              '&:hover': {
                backgroundColor: 'rgba(55, 53, 47, 0.08)',
                color: '#000000'
              },
              '&:active': {
                backgroundColor: 'rgba(55, 53, 47, 0.12)'
              }
            }}
            onClick={() => {
              setCurrentAction('add');
              if (onBulkTagClick) {
                onBulkTagClick();
              }
            }}
          >
            <TagOutlined style={{ fontSize: '14px' }} />
            {t('add')} {t('tags')}
          </Box>
        </Popover>
        
        {/* Remove Tags Button */}
        <Popover
          content={
            bulkRemoveTagsOpen && TagRemoveContent ? (
              <TagRemoveContent
                onConfirm={onBulkTagRemove}
                onCancel={() => {
                  setBulkRemoveTagsOpen(false);
                  setCurrentAction(null);
                }}
              />
            ) : null
          }
          trigger="click"
          placement="bottomLeft"
          open={bulkRemoveTagsOpen && currentAction === 'remove'}
          onOpenChange={(visible) => {
            if (!visible) {
              setBulkRemoveTagsOpen(false);
              setCurrentAction(null);
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              padding: '4px 10px',
              borderRadius: '4px',
              backgroundColor: 'rgba(55, 53, 47, 0.03)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              color: '#000000',
              height: '28px',
              '&:hover': {
                backgroundColor: 'rgba(235, 87, 87, 0.08)',
                color: '#eb5757'
              },
              '&:active': {
                backgroundColor: 'rgba(235, 87, 87, 0.12)'
              }
            }}
            onClick={() => {
              setCurrentAction('remove');
              if (onBulkRemoveTagClick) {
                onBulkRemoveTagClick();
              }
            }}
          >
            <Close style={{ fontSize: '14px' }} />
            {t('removeSelectedTags')}
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default SelectionActionBar;
